import React from "react";
import { MdAdd, MdComment } from "react-icons/md";
import Card from "../card";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";
import Pagination from "../common/Pagination";
import CommentItem from "./CommentItem";
import { Comment, PostAttachment } from "../../types/community";

interface CommentListProps {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  canManage: (comment: Comment) => boolean;
  likingComments: Record<number, boolean>;
  likeErrors: Record<number, string>;
  onAdd: () => void;
  onEdit: (comment: Comment) => void;
  onDelete: (comment: Comment) => void;
  onToggleLike: (comment: Comment) => void;
  onOpenLikers: (comment: Comment) => void;
  onImageClick: (attachments: PostAttachment[], index: number) => void;
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  loading,
  error,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  canManage,
  likingComments,
  likeErrors,
  onAdd,
  onEdit,
  onDelete,
  onToggleLike,
  onOpenLikers,
  onImageClick,
}) => {
  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
          Comments ({total})
        </h3>
        <button
          onClick={onAdd}
          className="flex items-center space-x-2 rounded-md bg-biafit-primary px-4 py-2 text-white transition-colors hover:bg-biafit-secondary"
        >
          <MdAdd className="h-4 w-4" />
          <span>Add Comment</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-200">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner bare size="md" />
        </div>
      )}

      {!loading && (!comments || comments.length === 0) && (
        <EmptyState
          icon={MdComment}
          title="No comments yet"
          description="Be the first to share your thoughts on this post."
          padding="sm"
        />
      )}

      {!loading && comments && comments.length > 0 && (
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              canManage={canManage(comment)}
              liking={!!likingComments[comment.id]}
              likeError={likeErrors[comment.id]}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleLike={onToggleLike}
              onOpenLikers={onOpenLikers}
              onImageClick={onImageClick}
            />
          ))}
        </div>
      )}

      {/* Comments Pagination */}
      <Pagination
        page={page}
        limit={limit}
        total={total}
        totalPages={totalPages}
        entityName="comments"
        onPageChange={onPageChange}
        loading={loading}
      />
    </Card>
  );
};

export default CommentList;
